import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import AppModal from '../common/AppModal';
import { formatCurrency } from '../../helpers/global';

const MODAL_CONFIG = {
  reconcile: {
    heading: 'Confirm Reconciliation',
    submitLabel: 'Confirm Reconciliation',
    submitButtonVariant: 'primary-new',
    instructionText: 'Please confirm the following before reconciling:',
    footerText:
      'Once confirmed, these invoices move to Reconciled History and the working capital remains in the main account.'
  },
  ignore: {
    heading: 'Confirm Ignore',
    submitLabel: 'Ignore Selected',
    submitButtonVariant: 'secondary',
    instructionText: null,
    footerText:
      'These invoices will be removed from pending reconciliation and will not appear in Reconciled History.'
  }
};

const MODAL_DIALOG_CLASS = 'confirm-reconcile-modal-dialog';

const ConfirmReconcileModal = ({
  show,
  onHide,
  onSubmit,
  submitting,
  invoiceCount,
  totalHardCost,
  totalCollectedFee,
  mode = 'reconcile'
}) => {
  const [qbCheckbox, setQbCheckbox] = useState(false);
  const [feesCheckbox, setFeesCheckbox] = useState(false);
  const isIgnoreMode = mode === 'ignore';
  const config = MODAL_CONFIG[mode] || MODAL_CONFIG.reconcile;

  useEffect(() => {
    if (show) {
      setQbCheckbox(false);
      setFeesCheckbox(false);
    }
  }, [show]);

  const canSubmit = isIgnoreMode || (qbCheckbox && feesCheckbox);

  const statCards = [
    {
      label: 'INVOICES',
      value: invoiceCount,
      background: 'bg-light-grey',
      valueClass: ''
    },
    {
      label: 'WORKING CAPITAL',
      value: formatCurrency(totalHardCost),
      background: 'bg-danger-light',
      valueClass: 'text-danger'
    },
    {
      label: 'COLLECTED FEES',
      value: formatCurrency(totalCollectedFee),
      background: 'bg-green-light',
      valueClass: 'text-success'
    }
  ];

  const checklistItems = [
    {
      id: 'qb',
      checked: qbCheckbox,
      setChecked: setQbCheckbox,
      checklistText: (
        <>
          <span className="font-weight-bold">Deposit confirmed in QuickBooks</span> — The client payment(s)
          have been matched to the corresponding invoices in QB.
        </>
      )
    },
    {
      id: 'fees',
      checked: feesCheckbox,
      setChecked: setFeesCheckbox,
      checklistText: (
        <>
          <span className="font-weight-bold">Collected fees transferred</span> — The collected fees (
          {formatCurrency(totalCollectedFee)}) have been transferred to the separate account.
        </>
      )
    }
  ];

  return (
    <AppModal
      show={show}
      onHide={onHide}
      onSubmit={onSubmit}
      heading={config.heading}
      submitting={submitting}
      submitDisabled={!canSubmit}
      submitLabel={config.submitLabel}
      submitButtonVariant={config.submitButtonVariant}
      dialogClassName={MODAL_DIALOG_CLASS}
    >
      <div className="mb-4 d-flex align-items-stretch" style={{ gap: 20 }}>
        {statCards.map((card, index) => (
          <div
            key={index}
            className={`text-center p-3 w-100 d-flex flex-column justify-content-center ${card.background}`}
            style={{
              borderRadius: 12,
              flex: '1 1 0',
              minWidth: 0
            }}
          >
            <div
              className="text-muted tinyFont font-weight-bold mb-1"
              style={{ minHeight: '2.4em', lineHeight: 1.2 }}
            >
              {card.label}
            </div>
            <div className={`xxlargeFont font-weight-bold mb-0 ${card.valueClass}`}>{card.value}</div>
          </div>
        ))}
      </div>

      {!isIgnoreMode && (
        <>
          <div className="xsmallFont text-muted">{config.instructionText}</div>

          <div className="my-2 d-flex flex-column" style={{ gap: 8 }}>
            {checklistItems.map((item, index) => (
              <div
                key={index}
                className={`border hover-border-success hover-success-light px-3 py-2 ${
                  item.checked ? 'border-success bg-light' : ''
                }`}
                onClick={() => item.setChecked(!item.checked)}
                style={{ cursor: 'pointer', transition: 'all 0.15s', borderRadius: 12 }}
              >
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    checked={item.checked}
                    onChange={e => item.setChecked(e.target.checked)}
                    onClick={e => e.stopPropagation()}
                    className="mt-0 recon-check-item"
                    style={{ marginRight: 10 }}
                  />
                  <div className="xsmallFont">{item.checklistText}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className={`text-muted tinyFont ${isIgnoreMode ? 'mb-0' : ''}`}>{config.footerText}</div>
    </AppModal>
  );
};

export default ConfirmReconcileModal;
